import { useTranslation } from 'react-i18next'
import type { SuggestionIssue } from '../issues/suggestionKit'
import { EmptyState } from '../ui/empty-state'

/** A claim dossier as the pipeline reads it. */
export type ClaimTicket = SuggestionIssue & {
    status: string
    properties?:
        | (SuggestionIssue['properties'] & {
              stage?: string
              /**
               * The date the insurer's position is expected, stamped by the
               * producer from the organization's business rule. Deliberately an
               * absolute date and not a number of days: the deadline is a legal
               * value that differs per country (60 days under French
               * `dommages-ouvrage`, other regimes elsewhere), so the frontend
               * must never compute it.
               */
              insurer_deadline_at?: string
              declared_at?: string
              dossier?: Array<{ document_type_code: string; document_uid?: string | null }>
          })
        | null
}

/** Claim pipeline columns — declared + awaiting_position share one column. */
export const STAGE_COLUMNS = ['constitution', 'declared', 'offer', 'settled'] as const
export type ClaimStage = (typeof STAGE_COLUMNS)[number]

export function stageOf(c: ClaimTicket): ClaimStage {
    const s = c.properties?.stage ?? 'constitution'
    if (s === 'awaiting_position') return 'declared'
    if (s === 'settled' || s === 'contested' || s === 'litigation') return 'settled'
    if (s === 'offer') return 'offer'
    if (s === 'declared') return 'declared'
    return 'constitution'
}

/**
 * Days left before the insurer is expected to take position.
 *
 * Reads a deadline **carried by the ticket** — never a duration added in the
 * browser. The previous implementation added a hardcoded 60 days to
 * `declared_at`, which is the French `dommages-ouvrage` clock (art. L242-1) and
 * silently wrong in every other jurisdiction. The number of days already lives
 * in an editable business rule server side
 * (`jobsrun.CoverageSweepRun.findOverdueClaims`); the ticket carries the
 * resulting date. No date on the ticket ⇒ no countdown, never a fallback.
 */
export function insurerCountdown(c: ClaimTicket): number | null {
    if (stageOf(c) !== 'declared') return null
    const deadline = c.properties?.insurer_deadline_at ?? c.due_date
    if (!deadline) return null
    const at = new Date(deadline).getTime()
    if (Number.isNaN(at)) return null
    return Math.ceil((at - Date.now()) / 86_400_000)
}

export function ClaimPipeline({
    claims,
    selected,
    onSelect,
}: {
    claims: ClaimTicket[]
    selected: string | null
    onSelect: (uid: string) => void
}) {
    const { t } = useTranslation('issues')

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="steering-pipeline">
            {STAGE_COLUMNS.map((col) => {
                const cards = claims.filter((c) => stageOf(c) === col)
                return (
                    <div key={col} className="border rounded-lg p-2.5 min-h-[80px]" data-testid={`steering-pipeline-${col}`}>
                        <div className="text-xs text-muted-foreground mb-2">
                            {t(`claim.stage_${col}`)} ({cards.length})
                        </div>
                        <div className="space-y-2">
                            {cards.map((c) => {
                                const pieces = c.properties?.dossier ?? []
                                const present = pieces.filter((p) => p.document_uid).length
                                const countdown = insurerCountdown(c)
                                return (
                                    <button
                                        key={c.uid}
                                        className={`w-full text-left rounded-md p-2 transition-colors ${
                                            selected === c.uid ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-muted/50 hover:bg-muted'
                                        }`}
                                        onClick={() => onSelect(c.uid)}
                                        data-testid={`steering-claim-${c.uid}`}
                                    >
                                        <div className="text-xs text-foreground line-clamp-2">{c.title}</div>
                                        {col === 'constitution' && pieces.length > 0 && (
                                            <div className="text-[11px] text-muted-foreground mt-1">
                                                {t('steering.pieces_count', { present, total: pieces.length })}
                                            </div>
                                        )}
                                        {countdown != null && (
                                            <div
                                                className={`text-[11px] mt-1 ${countdown < 10 ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground'}`}
                                                data-testid={`steering-claim-countdown-${c.uid}`}
                                            >
                                                {t('steering.insurer_countdown', { count: countdown })}
                                            </div>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                        {cards.length === 0 && (
                            <EmptyState variant="inline" className="py-2" description={t('steering.pipeline_column_empty')} />
                        )}
                    </div>
                )
            })}
        </div>
    )
}
